import React, { useRef, useState } from 'react';
import { Box, ButtonBase, CircularProgress, IconButton, TextField } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

export default function ActionTrackingBubble({ label, count, onRename, onOpenEvents, preparing = false,
  showCount = Number.isFinite(count), testId = 'dag-tracking-step', t }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const renameForDraft = useRef(null);
  const isPreparing = preparing || saving;
  const canOpenEvents = Boolean(onOpenEvents && Number.isFinite(count));
  const startEditing = () => {
    if (!onRename || editing || isPreparing) return;
    setDraft(label);
    renameForDraft.current = onRename;
    setError(false);
    setEditing(true);
  };
  const save = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (saving || !draft.trim()) return;
    if (draft.trim() === label) { setEditing(false); return; }
    setSaving(true);
    try {
      const saved = await renameForDraft.current(draft.trim());
      if (saved) setEditing(false);
      else setError(true);
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  };
  return <Box component="span" className="nodrag nopan" data-testid={testId}
    role={isPreparing ? 'status' : undefined}
    aria-busy={isPreparing} aria-live={isPreparing ? 'polite' : undefined}
    onClick={(event) => event.stopPropagation()}
    onPointerDown={(event) => event.stopPropagation()}
    onMouseDown={(event) => event.stopPropagation()}
    onKeyDown={(event) => {
      event.stopPropagation();
      if (editing && event.key === 'Escape' && !saving) { event.preventDefault(); setEditing(false); }
    }}
    sx={{ position: 'absolute', bottom: 'calc(100% + 12px)', left: '50%', transform: 'translateX(-50%)',
      width: editing ? '260px' : 'max-content', maxWidth: '260px', boxSizing: 'border-box', px: 1.75, py: 1,
      borderRadius: '16px', backgroundColor: '#edf8f4', border: '1px solid #75b8a6', color: '#174f43',
      boxShadow: '0 4px 14px rgba(16, 78, 67, 0.10), 0 1px 3px rgba(16, 78, 67, 0.06)', textAlign: 'center',
      fontSize: '16px', fontWeight: 600, lineHeight: '22px', whiteSpace: 'normal',
      overflowWrap: 'anywhere', pointerEvents: 'auto', zIndex: 1,
      ...(isPreparing ? { minWidth: '200px', background: 'linear-gradient(110deg, #d5f1e9 20%, #f0fff9 45%, #d5f1e9 70%)',
        backgroundSize: '250% 100%', animation: 'trackingBubbleShimmer 2.2s ease-in-out infinite',
        '@keyframes trackingBubbleShimmer': { '0%, 100%': { backgroundPosition: '100% 0' }, '50%': { backgroundPosition: '0% 0' } },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' } } : {}),
      '&:focus-visible': { outline: '2px solid #104e43', outlineOffset: 3 },
      '&::after': { content: '""', position: 'absolute', width: 10, height: 10,
        bottom: -6, left: 'calc(50% - 5px)', backgroundColor: '#edf8f4',
        borderRight: '1px solid #75b8a6', borderBottom: '1px solid #75b8a6', transform: 'rotate(45deg)' } }}>
    {isPreparing ? <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
        {[0, 1, 2].map(index => <Box component="span" key={index} sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#23816b',
          animation: 'trackingBubbleDot 1.2s ease-in-out infinite', animationDelay: `${index * 0.15}s`,
          '@keyframes trackingBubbleDot': { '0%, 70%, 100%': { transform: 'translateY(0)', opacity: 0.45 },
            '35%': { transform: 'translateY(-4px)', opacity: 1 } },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 1 } }} />)}
      </Box>
      {t('actionFunnel.preparing')}
    </Box> : editing ? <Box component="form" onSubmit={save}>
      <TextField autoFocus fullWidth multiline size="small" label={t('actionFunnel.stepName')}
        value={draft} disabled={saving} onChange={event => { setDraft(event.target.value); setError(false); }}
        onFocus={event => event.target.select()} inputProps={{ maxLength: 120 }}
        error={error || !draft.trim()} helperText={error ? t('actionFunnel.toggleError') : !draft.trim() ? t('actionFunnel.nameRequired') : undefined}
        onKeyDown={event => {
          if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) save(event);
        }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
        <IconButton size="small" disabled={saving} aria-label={t('actionFunnel.cancelRename')}
          onClick={() => setEditing(false)}><CloseRoundedIcon fontSize="small" /></IconButton>
        <IconButton size="small" type="submit" disabled={saving || !draft.trim()} aria-label={t('actionFunnel.saveName')}>
          {saving ? <CircularProgress size={20} /> : <CheckRoundedIcon fontSize="small" />}
        </IconButton>
      </Box>
    </Box> : <><Box component={onRename ? ButtonBase : 'span'}
      onClick={onRename ? startEditing : undefined}
      aria-label={onRename ? t('actionFunnel.renameStep') : undefined}
      sx={{ display: 'inline', font: 'inherit', color: 'inherit', textAlign: 'inherit',
        borderRadius: 0.5, cursor: onRename ? 'text' : 'inherit',
        '&.Mui-focusVisible': { outline: '2px solid #104e43', outlineOffset: 3 } }}>{label}</Box>{showCount && <>
      <Box component={canOpenEvents ? ButtonBase : 'span'}
        onClick={canOpenEvents ? (event) => { event.stopPropagation(); onOpenEvents(); } : undefined}
        aria-label={canOpenEvents ? t('actionFunnel.viewEvents', { name: label }) : undefined}
        sx={{ display: 'inline-block', ml: 1, px: 1, borderRadius: '9px', font: 'inherit',
          backgroundColor: '#104e43', color: '#fff', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
          '&:hover': canOpenEvents ? { backgroundColor: '#1b6959' } : undefined,
          '&.Mui-focusVisible': { outline: '2px solid #104e43', outlineOffset: 3 } }}>
        {Number.isFinite(count) ? count.toLocaleString() : t('workspaceHome.unavailable', 'Unavailable')}
      </Box>
      <Box component="span" sx={{ display: 'block', fontSize: '13px', lineHeight: '18px', mt: 0.25 }}>
        {t('workspaceHome.periods.7', 'Last 7 days')}
      </Box>
    </>}</>}
  </Box>;
}
